import React, { useState, useEffect } from 'react';
import Chip from '@solar/common/Chip/Chip';
import CloseIcon from '@solar/assets/images/action/Close';
import {
  AmenitiesFriendlyNames,
  HotelStarsFriendlyNames,
  VibesFriendlyNames,
  GuestRatingFriendlyNames,
  SpaProductTypes,
  PoolProductTypes,
  useSearchContext,
} from '@context/SearchContext2025';
import cn from '@helpers/className';
import { SearchState } from '@customTypes/search';

type Props = {
  className?: string;
  onChange?: (s: SearchState) => void;
};

type ChipData = {
  label: string;
  value: any;
};

export default function SRPAppliedFilterChips({ className = '', onChange = () => {} }: Props) {
  const searchContext = useSearchContext();
  const { searchParams } = searchContext;
  const { filters } = searchParams;

  const [fireOnChange, setFireOnChange] = useState(false);
  useEffect(() => {
    if (fireOnChange) {
      onChange(searchParams);
      setFireOnChange(false);
    }
  }, [fireOnChange, searchParams, onChange]);

  // -------------------------------------------------------------------
  // Get all of the applied filters ------------------------------------
  // -------------------------------------------------------------------
  const amenities: Array<ChipData> = Object.entries(AmenitiesFriendlyNames)
    .filter(([value]) => filters.amenities[value as keyof typeof filters.amenities])
    .map(([value, label]) => ({ label, value }));

  const vibes: Array<ChipData> = Object.entries(VibesFriendlyNames)
    .filter(([value]) => filters.vibes[value as keyof typeof filters.vibes])
    .map(([value, label]) => ({ label, value }));

  const hotelStars: Array<ChipData> = Object.entries(HotelStarsFriendlyNames)
    .filter(([value]) => filters.hotel_stars[value as keyof typeof filters.hotel_stars])
    .map(([value, label]) => ({ label, value }));

  const guestRatings: Array<ChipData> = Object.entries(GuestRatingFriendlyNames)
    .filter(([value]) => filters.guest_ratings[value as keyof typeof filters.guest_ratings])
    .map(([value, label]) => ({ label, value }));

  const spaProductTypes: Array<ChipData> = Object.entries(SpaProductTypes)
    .filter(([, value]) => filters.product_types.includes(value))
    .map(([label, value]) => ({ label, value }));

  const poolProductTypes: Array<ChipData> = Object.entries(PoolProductTypes)
    .filter(([, value]) => filters.product_types.includes(value))
    .map(([label, value]) => ({ label, value }));

  const isAnyFilterApplied = [
    amenities,
    vibes,
    hotelStars,
    guestRatings,
    poolProductTypes,
    spaProductTypes,
  ].some((arr) => arr.length > 0);

  const getAmenitiesSetter = (value: keyof SearchState['filters']['amenities']) => () => {
    searchContext.setAmenities({
      [value]: !filters.amenities[value],
    });
    setFireOnChange(true);
  };

  const getHotelClassSetter = (value: keyof SearchState['filters']['hotel_stars']) => () => {
    searchContext.setHotelStars({
      [value]: !filters.hotel_stars[value],
    });
    setFireOnChange(true);
  };

  const getGuestRatingsSetter = (value: keyof SearchState['filters']['guest_ratings']) => () => {
    searchContext.setGuestRatings({
      [value]: !filters.guest_ratings[value],
    });
    setFireOnChange(true);
  };

  const getVibesSetter = (value: keyof SearchState['filters']['vibes']) => () => {
    searchContext.setVibes({
      [value]: !filters.vibes[value],
    });
    setFireOnChange(true);
  };

  return (
    <div
      className={cn({
        'flex flex-wrap gap-2': true,
        [className]: !!className,
        'hidden desktop:hidden': !isAnyFilterApplied,
      })}
    >
      {poolProductTypes.map((item) => (
        <Chip
          key={item.value}
          className="flex-grow-0 flex-shrink-0"
          text={item.label}
          onClick={() => {
            searchContext.removeProductType(item.value);
            setFireOnChange(true);
          }}
          rightIcon={<CloseIcon />}
        />
      ))}

      {spaProductTypes.map((item) => (
        <Chip
          key={item.value}
          className="flex-grow-0 flex-shrink-0"
          text={item.label}
          onClick={() => {
            searchContext.removeProductType(item.value);
            setFireOnChange(true);
          }}
          rightIcon={<CloseIcon />}
        />
      ))}

      {amenities.map((item) => (
        <Chip
          key={item.value}
          className="flex-grow-0 flex-shrink-0"
          text={item.label}
          onClick={getAmenitiesSetter(item.value)}
          rightIcon={<CloseIcon />}
        />
      ))}

      {hotelStars.map((item) => (
        <Chip
          key={item.value}
          className="flex-grow-0 flex-shrink-0"
          text={item.label}
          onClick={getHotelClassSetter(item.value)}
          rightIcon={<CloseIcon />}
        />
      ))}

      {guestRatings.map((item) => (
        <Chip
          key={item.value}
          className="flex-grow-0 flex-shrink-0"
          text={item.label}
          onClick={getGuestRatingsSetter(item.value)}
          rightIcon={<CloseIcon />}
        />
      ))}

      {vibes.map((item) => (
        <Chip
          key={item.value}
          className="flex-grow-0 flex-shrink-0"
          text={item.label}
          onClick={getVibesSetter(item.value)}
          rightIcon={<CloseIcon />}
        />
      ))}
    </div>
  );
}
